// Empire Apps Chassis · connectors/financial/credit-report
// CIBIL + Experian + Equifax + CRIF — bureau-agnostic PDF upload.
// Detects bureau from PDF header text, returns normalised CreditReport.

import {
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  type CreditReport,
  fail,
  isoTs,
  ok,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';
import { getPdfBridge } from './adobe-pdf-bridge.ts';

const STATUS_KEY = 'empire:financial:credit-report:status';

interface PersistedStatus {
  readonly connected: boolean;
  readonly bureau: string | null;
  readonly lastSync: string | null;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, bureau: null, lastSync: null });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

function detectBureau(text: string): CreditReport['bureau'] {
  if (/CIBIL/i.test(text)) return 'CIBIL';
  if (/Experian/i.test(text)) return 'Experian';
  if (/Equifax/i.test(text)) return 'Equifax';
  if (/CRIF\s+High/i.test(text)) return 'CRIF';
  return 'CIBIL';
}

function scoreTier(score: number): CreditReport['tier'] {
  if (score >= 750) return 'excellent';
  if (score >= 700) return 'good';
  if (score >= 600) return 'fair';
  return 'poor';
}

export function parseCreditPdfText(text: string): CreditReport | null {
  const bureau = detectBureau(text);
  const scoreMatch = text.match(/(?:Score|CIBIL\s+Score|Experian\s+Score|TransUnion\s+CIBIL)\s*[:=]?\s*(\d{3})/i);
  const score = scoreMatch ? Number(scoreMatch[1]) : 0;
  if (score < 300 || score > 900) return null;
  const tradelinesMatch = text.match(/(?:Total\s+Accounts|Tradelines)\s*[:=]?\s*(\d{1,3})/i);
  const enquiriesMatch = text.match(/(?:Enquiries|Inquiries)\s*(?:in\s+last\s+30\s+days)?\s*[:=]?\s*(\d{1,3})/i);
  const utilMatch = text.match(/(?:Utili[sz]ation)\s*[:=]?\s*([\d.]+)\s*%/i);
  return {
    bureau,
    score,
    tier: scoreTier(score),
    tradelines: Number(tradelinesMatch?.[1] ?? 0),
    enquiries30d: Number(enquiriesMatch?.[1] ?? 0),
    utilizationPct: Number(utilMatch?.[1] ?? 0),
    asOf: isoTs(),
  };
}

function mockReport(): CreditReport {
  return {
    bureau: 'CIBIL',
    score: 782,
    tier: 'excellent',
    tradelines: 8,
    enquiries30d: 2,
    utilizationPct: 18,
    asOf: isoTs(),
  };
}

export const creditReport: Connector = {
  id: 'credit-report',
  displayName: 'Credit Bureau report upload (CIBIL/Experian/Equifax/CRIF)',
  category: 'credit',
  defaultMode: 'mock',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: 'mock',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.connected ? 1 : 0,
      disabledReason: null,
    };
  },

  async connect(ctx: ConnectorContext, opts?: Record<string, string>): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    logConnectorConsent(ctx.dpdpLog, 'connector-credit-upload', 'Bureau report PDF');
    const connectedAt = isoTs();
    writeStatus({ connected: true, bureau: opts?.bureau ?? null, lastSync: connectedAt });
    return ok({ connectedAt });
  },

  async import(_ctx: ConnectorContext, opts?: { readonly fileBlob?: Blob }): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected.', true);
    if (opts?.fileBlob) {
      const bridge = getPdfBridge();
      const extract = await bridge.extract({ blob: opts.fileBlob });
      const rep = parseCreditPdfText(extract.textFull);
      if (rep) {
        writeStatus({ ...s, lastSync: isoTs(), bureau: rep.bureau });
        return ok({ credit: rep });
      }
    }
    const r = mockReport();
    writeStatus({ ...s, lastSync: isoTs(), bureau: r.bureau });
    return ok({ credit: r });
  },

  async disconnect(ctx: ConnectorContext): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, bureau: null, lastSync: null });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-credit-upload' });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData(): ConnectorPayload {
    return { credit: mockReport() };
  },
};
